/**
 * Centralized sessionStorage keys for the form-builder consumer flow.
 *
 * These keys are written and read in 4+ different places across
 * Parrot — `FormView`, `AppLayout` (beforeunload handler),
 * `useFinishSubmissions` (carrier-flow return), and
 * `CarrierConnectedView` (back-stash). A typo in any one of those
 * touchpoints silently breaks the round-trip (e.g. carrier-flow
 * return navigation just falls through to `/success` because the
 * read site spelled the key wrong). Keeping the literals here is the
 * single source of truth.
 *
 * Lifetime of each:
 *
 * - `FORM_BUILDER_RETURN_KEY` — written by `FormView.handleCarrierHandoff`
 *   when handing off to the legacy carrier flow; read by
 *   `useFinishSubmissions.finish()` to return the consumer back to
 *   the form-builder URL with `?step=<resumeStepId>`. NOT cleared on
 *   read — finish() runs from several touchpoints (FetchingView
 *   auto-skip, CarrierConnectedView "No", SubmissionOverview Continue)
 *   and each one needs to route back into the form. Next handoff
 *   overwrites the value; on tab close sessionStorage clears.
 * - `INTENTIONAL_REDIRECT_KEY` — set by any code path that's about to
 *   call `window.location.href` so AppLayout's `beforeunload` handler
 *   doesn't show the consumer a "Leave site?" dialog. Single-use:
 *   AppLayout removes it on every mount.
 * - `FORM_SUBMITTED_KEY` — set by `FormView.handleSubmit` after a
 *   successful POST. AppLayout's `beforeunload` reads it to suppress
 *   abandonment tracking; CompleteBody reads it on mount to suppress
 *   a duplicate auto-submit if the consumer hits browser-back from
 *   a `redirect_url` destination.
 * - `FORM_BUILDER_BACK_KEY` — written by `CarrierConnectedView` right
 *   before the consumer is sent onward to /fetching → form, so the
 *   form-builder's back-chevron can land them on /connected directly
 *   (vs. the intermediate /fetching page that browser history would
 *   surface). Read + cleared by `FormView.handleBack`.
 * - `FORM_BUILDER_HANDOFF_BACK_KEY` — written by
 *   `FormView.handleCarrierHandoff` with the form URL + `?step=` of
 *   the pre-carrier step so legacy `CarrierSelectView.onBack` and
 *   `SubmissionOverview.onBack` can return the consumer to the form
 *   instead of the legacy /landing page. Read + cleared by those
 *   onBack handlers when they actually take the form-return path.
 *   Survives `useFinishSubmissions.finish()` — the auto-skip on
 *   `FetchingView` fires within seconds of carrier connection and
 *   the consumer may still want to back-out via /connected → form
 *   after that, so clearing too eagerly breaks the loop. Next
 *   handoff overwrites the value; on tab close sessionStorage clears.
 */

export const FORM_BUILDER_RETURN_KEY = 'insurgrid_form_builder_return';
export const INTENTIONAL_REDIRECT_KEY = 'insurgrid_intentional_redirect';
export const FORM_SUBMITTED_KEY = 'insurgrid_form_submitted';
export const FORM_BUILDER_BACK_KEY = 'insurgrid_form_builder_back';
export const FORM_BUILDER_HANDOFF_BACK_KEY = 'insurgrid_form_builder_handoff_back';
// Public ID of a pure-carrier form-builder form (INS-845) — set by
// `FormView.handleCarrierHandoff` whenever the auto-handoff fires (i.e.
// when there are no visible steps in FormView), and read by
// `useFinishSubmissions.finish()` to fire the final flush + toast
// without ever sending the consumer back through FormView. Cleared by
// finish() on read so a subsequent non-form session in the same tab
// can't accidentally trigger a phantom submit.
export const FORM_BUILDER_PURE_CARRIER_KEY = 'insurgrid_form_builder_pure_carrier';
// Line of business ('personal' | 'commercial') the agent picked in the
// Form Builder sidebar. Written by `FormView.handleCarrierHandoff`
// before the legacy carrier-flow redirect; read by `useLineType` as
// the priority override above the subdomain heuristic. Lifetime is
// tied to `FORM_BUILDER_HANDOFF_BACK_KEY` — cleared at the same
// touchpoints (CarrierSelectView/SubmissionOverview back-to-form,
// useFinishSubmissions bad-value fallback) so it can't poison a
// subsequent non-form custom-link session on the same tab.
export const FORM_INSURANCE_LINE_KEY = 'insurgrid_form_insurance_line';
// `1` while the form-builder consumer is on the Welcome step (the
// landing-equivalent screen that shows the "Powered by InsurGrid"
// footer). Cleared the moment they advance — subsequent steps mirror
// the legacy /submission/create/... pages and run without the footer.
export const FORM_ON_WELCOME_KEY = 'insurgrid_form_on_welcome';
// INS-1214: JSON snapshot of the carrier connections that already existed when
// this form first handed off to the legacy carrier flow, tagged with the form's
// public id — `{"f": "<public_id>", "u": ["<uuid>", …] | null}`. Written by
// `FormView.handleCarrierHandoff`, read on the final flush by
// `carrierSessionLink.newCarrierSubmissionUuids` so Phoenix is told which
// submissions belong to *this* response and not to some earlier session. `u:
// null` records that the list was unknown at hand-off, which makes the read
// side send nothing rather than guess. Deliberately NOT cleared: re-submitting
// the same form in the same tab upserts the same response row, so the original
// "before" stays the correct baseline; a different form is ignored via `f`.
export const CARRIER_SNAPSHOT_KEY = 'insurgrid_form_carrier_snapshot';
// JSON-encoded chrome-visibility flags for the currently-mounted form-
// builder consumer flow. Written by `FormView` on mount and removed on
// unmount; read by `NavHeader` (and any future chrome host) to hide
// InsurGrid branding when the form's agent opted out. Carried as a
// sessionStorage key rather than React context because `NavHeader`
// renders inside `[agentSlug]/layout.tsx` (a SERVER layout) while
// `FormView` is the page client, and a context provider can't cross
// that boundary. Shape: `{ hideAgencyLogo?: boolean,
// hideAgentHeadshot?: boolean }`. Paired with the
// `form-branding-changed` window event so subscribers re-read on
// mount/unmount without prop drilling. */
export const FORM_BRANDING_KEY = 'insurgrid_form_branding';

/** Dispatched on `window` whenever `FormView` writes or removes
 *  FORM_BRANDING_KEY. Layout-level chrome components (NavHeader,
 *  AppContainer, ConnectAppLayout, AppLoadingContainer) subscribe to
 *  re-read the snapshot. Kept as a single const so a typo in any
 *  subscriber doesn't silently break the live-update channel. */
export const FORM_BRANDING_CHANGED_EVENT = 'form-branding-changed';

/** True when the URL is a form-builder consumer route
 *  (`/{agentSlug}/form/...`). Centralizes the segment-check that
 *  AppContainer, NavHeader, and ConnectAppLayout each used to redo
 *  inline — one source of truth so renaming the route doesn't leave
 *  any chrome component out of sync. */
export function isFormBuilderPath(pathname: string | null | undefined): boolean {
  return pathname?.split('/')[2] === 'form';
}

/** Read `buttonColor` out of `FORM_BRANDING_KEY` once. Returns `null` if
 *  the key is missing or no buttonColor was written (e.g. legacy /landing
 *  or /submission/... routes where no FormView mounted on this tab). */
export function readFormBrandingButtonColor(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(FORM_BRANDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { buttonColor?: unknown };
    return typeof parsed.buttonColor === 'string' && parsed.buttonColor
      ? parsed.buttonColor
      : null;
  } catch {
    return null;
  }
}
